import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  ThumbsUp, 
  Clock, 
  TrendingUp, 
  MessageCircle, 
  User, 
  UserX, 
  UserCog,
  Paperclip, 
  Download,
  CheckCircle,
  AlertCircle,
  Eye,
  X,
  Loader,
  Upload,
  FileText,
  Edit,
  Trash2
} from 'lucide-react';
import { suggestionService } from './suggestionService';
import { uploadService } from '../../lib/uploadService';
import { useAuth } from '../../hooks/useAuth';
import { Suggestion } from '../../types';
import { DocumentSnapshot } from 'firebase/firestore';

// Edit Suggestion Modal Component
interface EditSuggestionModalProps {
  isOpen: boolean;
  suggestion: Suggestion | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditSuggestionModal: React.FC<EditSuggestionModalProps> = ({ isOpen, suggestion, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (suggestion) {
      setFormData({
        title: suggestion.title,
        content: suggestion.content
      });
      setAttachmentFile(null);
      setRemoveExistingFile(false);
    }
  }, [suggestion]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = uploadService.validateSuggestionFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setAttachmentFile(file);
    setRemoveExistingFile(false); // If adding new file, don't remove existing
    setError('');
  };

  const handleRemoveNewFile = () => {
    setAttachmentFile(null);
  };

  const handleRemoveExistingFile = () => {
    setRemoveExistingFile(true);
    setAttachmentFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggestion || !user) return;

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and description are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let fileUrl = suggestion.fileUrl;
      let fileName = suggestion.fileName;
      let fileSize = suggestion.fileSize;

      // Handle file changes
      if (attachmentFile) {
        // Upload new file
        try {
          const uploadResult = await uploadService.uploadSuggestionFile(attachmentFile);
          fileUrl = uploadResult.url;
          fileName = uploadResult.name;
          fileSize = uploadResult.size;
        } catch (uploadError: any) {
          setError(`Failed to upload file: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      } else if (removeExistingFile) {
        // Remove existing file
        fileUrl = undefined;
        fileName = undefined;
        fileSize = undefined;
      }

      // Update suggestion
      await suggestionService.updateSuggestion(suggestion.id, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        fileUrl,
        fileName,
        fileSize
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to update suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError('');
    setAttachmentFile(null);
    setRemoveExistingFile(false);
    onClose();
  };

  if (!isOpen || !suggestion) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Edit Suggestion</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Brief summary of your suggestion"
                required
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.title.length}/200 characters
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Description *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                className="w-full p-3 border border-border rounded-lg h-32 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary resize-none"
                placeholder="Describe your suggestion in detail..."
                required
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.content.length}/2000 characters
              </p>
            </div>

            {/* File Management */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Attachment</label>
              
              {/* Existing File */}
              {suggestion.fileUrl && suggestion.fileName && !removeExistingFile && !attachmentFile && (
                <div className="border rounded-lg p-4 bg-muted mb-3">
                  <div className="flex items-center gap-3">
                    <FileText className="text-primary" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">Current: {suggestion.fileName}</p>
                      <p className="text-sm text-muted-foreground">
                        {suggestion.fileSize && (suggestion.fileSize / 1024 / 1024).toFixed(2) + ' MB'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveExistingFile}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove current file"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* New File Upload */}
              {!attachmentFile && (!suggestion.fileUrl || removeExistingFile) && (
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                  <p className="text-muted-foreground mb-2">
                    Upload a new file to support your suggestion
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Images, PDFs, Office documents - Max 10MB
                  </p>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="edit-suggestion-file"
                  />
                  <label
                    htmlFor="edit-suggestion-file"
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded cursor-pointer hover:bg-secondary/80 transition-colors"
                  >
                    Choose File
                  </label>
                </div>
              )}

              {/* New File Selected */}
              {attachmentFile && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-3">
                    <FileText className="text-green-600" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">New: {attachmentFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(attachmentFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveNewFile}
                      className="text-muted-foreground hover:text-red-600 p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* File Removed Message */}
              {removeExistingFile && !attachmentFile && (
                <div className="border rounded-lg p-4 bg-red-50">
                  <p className="text-red-700 text-sm">
                    Current file will be removed when you save changes.
                  </p>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-accent disabled:opacity-50 text-foreground"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.title.trim() || !formData.content.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {attachmentFile ? 'Uploading...' : 'Updating...'}
                  </>
                ) : (
                  'Update Suggestion'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Admin Management Modal Component
interface AdminManageModalProps {
  isOpen: boolean;
  suggestion: Suggestion | null;
  onClose: () => void;
  onSuccess: () => void;
  onDelete: (suggestionId: string) => void;
}

const AdminManageModal: React.FC<AdminManageModalProps> = ({ isOpen, suggestion, onClose, onSuccess, onDelete }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Suggestion['status']>('open');
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (suggestion) {
      setSelectedStatus(suggestion.status);
      setResponse(suggestion.adminResponse || '');
    }
  }, [suggestion]);

  const handleDelete = () => {
    if (!suggestion) return;
    
    if (window.confirm('Are you sure you want to delete this suggestion? This action cannot be undone.')) {
      onDelete(suggestion.id);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggestion || !user) return;

    setSubmitting(true);
    setError('');

    try {
      await suggestionService.updateSuggestionStatus(
        suggestion.id,
        selectedStatus,
        response.trim() || undefined,
        user.email!
      );

      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to update suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError('');
    onClose();
  };

  const getStatusColor = (status: Suggestion['status']) => {
    switch (status) {
      case 'open': return 'text-blue-600';
      case 'under-review': return 'text-yellow-600';
      case 'implemented': return 'text-green-600';
      case 'declined': return 'text-red-600';
    }
  };

  if (!isOpen || !suggestion) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Manage Suggestion</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Suggestion Details */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-foreground mb-2">{suggestion.title}</h3>
            <p className="text-sm text-muted-foreground mb-2">
              By: {suggestion.isAnonymous ? 'Anonymous' : suggestion.createdByName || suggestion.createdBy}
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              Created: {suggestion.createdAt.toLocaleDateString()}
            </p>
            <p className="text-sm text-muted-foreground mb-3">
              Upvotes: {suggestion.upvoteCount || 0}
            </p>
            <div className="text-foreground text-sm">
              {suggestion.content.split('\n').map((line, index) => (
                <p key={index} className={index > 0 ? 'mt-2' : ''}>
                  {line}
                </p>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Status Selection */}
            <div>
              <label className="block text-sm font-medium mb-3 text-foreground">Status</label>
              <div className="grid grid-cols-2 gap-3">
                {([
                  { value: 'open', label: 'Open', icon: MessageCircle },
                  { value: 'under-review', label: 'Under Review', icon: Eye },
                  { value: 'implemented', label: 'Implemented', icon: CheckCircle },
                  { value: 'declined', label: 'Declined', icon: AlertCircle }
                ] as const).map(({ value, label, icon: Icon }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedStatus === value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={value}
                      checked={selectedStatus === value}
                      onChange={(e) => setSelectedStatus(e.target.value as Suggestion['status'])}
                      className="sr-only"
                    />
                    <Icon className={getStatusColor(value)} size={20} />
                    <span className={`font-medium ${selectedStatus === value ? 'text-primary' : 'text-foreground'}`}>
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Admin Response */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">
                Admin Response {selectedStatus !== 'open' && '(Required for status changes)'}
              </label>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="w-full p-3 border border-border rounded-lg h-24 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary resize-none"
                placeholder={
                  selectedStatus === 'implemented' 
                    ? 'Explain how this suggestion was implemented...'
                    : selectedStatus === 'declined'
                    ? 'Explain why this suggestion was declined...'
                    : selectedStatus === 'under-review'
                    ? 'Update on the review progress...'
                    : 'Optional response to the suggestion...'
                }
                maxLength={1000}
                required={selectedStatus !== 'open'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {response.length}/1000 characters
              </p>
            </div>

            {/* Previous Response */}
            {suggestion.adminResponse && suggestion.adminResponseBy && suggestion.adminResponseAt && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Previous Admin Response</h4>
                <p className="text-sm text-blue-700 mb-2">{suggestion.adminResponse}</p>
                <p className="text-xs text-blue-600">
                  By {suggestion.adminResponseBy} on {suggestion.adminResponseAt.toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <AlertCircle size={16} />
                Delete
              </button>
              
              <div className="flex-1 flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-accent disabled:opacity-50 text-foreground"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (selectedStatus !== 'open' && !response.trim())}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {submitting ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Updating...
                    </>
                  ) : (
                    'Update Suggestion'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Add Suggestion Modal Component
interface AddSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddSuggestionModal: React.FC<AddSuggestionModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user, appUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isAnonymous: false
  });
  const [error, setError] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = uploadService.validateSuggestionFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setAttachmentFile(file);
    setError('');
  };

  const handleRemoveFile = () => {
    setAttachmentFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !appUser) return;

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and description are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let fileUrl = '';
      let fileName = '';
      let fileSize = 0;

      // Upload attachment if selected
      if (attachmentFile) {
        try {
          const uploadResult = await uploadService.uploadSuggestionFile(attachmentFile);
          fileUrl = uploadResult.url;
          fileName = uploadResult.name;
          fileSize = uploadResult.size;
        } catch (uploadError: any) {
          setError(`Failed to upload file: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      }

      // Create suggestion
      await suggestionService.addSuggestion({
        title: formData.title.trim(),
        content: formData.content.trim(),
        isAnonymous: formData.isAnonymous,
        createdBy: user.email!,
        createdByName: formData.isAnonymous ? undefined : appUser.displayName,
        fileUrl: fileUrl || undefined,
        fileName: fileName || undefined,
        fileSize: fileSize || undefined,
      });

      // Reset form and close modal
      setFormData({ title: '', content: '', isAnonymous: false });
      setAttachmentFile(null);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({ title: '', content: '', isAnonymous: false });
    setAttachmentFile(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Add Suggestion</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Brief summary of your suggestion"
                required
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.title.length}/200 characters
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Description *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                className="w-full p-3 border border-border rounded-lg h-32 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary resize-none"
                placeholder="Describe your suggestion in detail. What problem does it solve? How would it improve our workflow?"
                required
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.content.length}/2000 characters
              </p>
            </div>

            {/* File Attachment */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Attachment (Optional)</label>
              {!attachmentFile ? (
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                  <p className="text-muted-foreground mb-2">
                    Upload a file to support your suggestion
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Images, PDFs, Office documents - Max 10MB
                  </p>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="suggestion-file"
                  />
                  <label
                    htmlFor="suggestion-file"
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded cursor-pointer hover:bg-secondary/80 transition-colors"
                  >
                    Choose File
                  </label>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted">
                  <div className="flex items-center gap-3">
                    <FileText className="text-primary" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{attachmentFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(attachmentFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="text-muted-foreground hover:text-red-600 p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Anonymous Option */}
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              <input
                type="checkbox"
                id="anonymous"
                checked={formData.isAnonymous}
                onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="anonymous" className="text-sm text-foreground cursor-pointer">
                Submit anonymously
              </label>
              <div className="ml-auto text-muted-foreground">
                {formData.isAnonymous ? <UserX size={16} /> : <User size={16} />}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-accent disabled:opacity-50 text-foreground"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.title.trim() || !formData.content.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {attachmentFile ? 'Uploading...' : 'Submitting...'}
                  </>
                ) : (
                  'Submit Suggestion'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Suggestion Card Component
interface SuggestionCardProps {
  suggestion: Suggestion;
  onUpvote: (suggestionId: string) => void;
  onAdminManage: (suggestion: Suggestion) => void;
  onUserEdit: (suggestion: Suggestion) => void;
  onUserDelete: (suggestionId: string) => void;
  currentUser: string;
  canManage: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion, onUpvote, onAdminManage, onUserEdit, onUserDelete, currentUser, canManage }) => {
  const hasUpvoted = suggestionService.hasUserUpvoted(suggestion, currentUser);
  const isCreator = suggestion.createdBy === currentUser;

  const getStatusIcon = (status: Suggestion['status']) => {
    switch (status) {
      case 'open': return <MessageCircle className="text-blue-500" size={16} />;
      case 'under-review': return <Eye className="text-yellow-500" size={16} />;
      case 'implemented': return <CheckCircle className="text-green-500" size={16} />;
      case 'declined': return <AlertCircle className="text-red-500" size={16} />;
    }
  };

  const getStatusColor = (status: Suggestion['status']) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'under-review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'implemented': return 'bg-green-100 text-green-800 border-green-200';
      case 'declined': return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const handleDownload = () => {
    if (suggestion.fileUrl && suggestion.fileName) {
      const link = document.createElement('a');
      link.href = suggestion.fileUrl;
      link.download = suggestion.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-foreground text-lg">{suggestion.title}</h3>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              {suggestion.isAnonymous ? <UserX size={14} /> : <User size={14} />}
              <span>
                {suggestion.isAnonymous ? 'Anonymous' : suggestion.createdByName || suggestion.createdBy}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{suggestion.createdAt.toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(suggestion.status)}`}>
          {getStatusIcon(suggestion.status)}
          <span className="capitalize">{suggestion.status.replace('-', ' ')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="text-foreground whitespace-pre-wrap">
        {suggestion.content}
      </div>

      {/* File Attachment */}
      {suggestion.fileUrl && suggestion.fileName && (
        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Paperclip size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground flex-1">{suggestion.fileName}</span>
            {suggestion.fileSize && (
              <span className="text-xs text-muted-foreground">
                ({(suggestion.fileSize / 1024 / 1024).toFixed(2)} MB)
              </span>
            )}
            <button
              onClick={handleDownload}
              className="text-primary hover:text-primary/80 p-1"
              title="Download attachment"
            >
              <Download size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Admin Response */}
      {suggestion.adminResponse && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <MessageCircle className="text-blue-500 mt-0.5" size={16} />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Admin Response</p>
              <p className="text-sm text-blue-700 mt-1">{suggestion.adminResponse}</p>
              {suggestion.adminResponseBy && suggestion.adminResponseAt && (
                <p className="text-xs text-blue-600 mt-2">
                  By {suggestion.adminResponseBy} on {suggestion.adminResponseAt.toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t">
        <button
          onClick={() => onUpvote(suggestion.id)}
          className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
            hasUpvoted 
              ? 'bg-primary text-primary-foreground' 
              : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
          }`}
        >
          <ThumbsUp size={16} />
          <span className="font-medium">{suggestion.upvoteCount || 0}</span>
        </button>

        <div className="flex items-center gap-2">
          {/* User Actions - Edit/Delete own suggestions */}
          {isCreator && (
            <>
              <button
                onClick={() => onUserEdit(suggestion)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-muted-foreground hover:bg-blue-100 hover:text-blue-600 border border-gray-200 hover:border-blue-200 text-xs"
              >
                <Edit size={12} />
                Edit
              </button>
              <button
                onClick={() => onUserDelete(suggestion.id)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-muted-foreground hover:bg-red-100 hover:text-red-600 border border-gray-200 hover:border-red-200 text-xs"
              >
                <Trash2 size={12} />
                Delete
              </button>
            </>
          )}

          {/* Admin Actions */}
          {canManage && (
            <button
              onClick={() => onAdminManage(suggestion)}
              className="flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-muted-foreground hover:bg-purple-100 hover:text-purple-600 border border-gray-200 hover:border-purple-200"
            >
              <UserCog size={16} />
              <span className="font-medium">Manage</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Suggestion Box Module
export const SuggestionBoxModule: React.FC = () => {
  const { user, permissions } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<'latest' | 'popular'>('latest');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [managingSuggestion, setManagingSuggestion] = useState<Suggestion | null>(null);
  const [editingSuggestion, setEditingSuggestion] = useState<Suggestion | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | undefined>();
  const [statusCounts, setStatusCounts] = useState({ open: 0, underReview: 0, implemented: 0, declined: 0 });

  // Load suggestions
  useEffect(() => {
    loadSuggestions(true);
    loadStatusCounts();
  }, [activeTab]);

  const loadSuggestions = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setSuggestions([]);
        setLastDoc(undefined);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const result = await suggestionService.getSuggestions({
        lastDoc: reset ? undefined : lastDoc,
        sortBy: activeTab
      });

      if (reset) {
        setSuggestions(result.suggestions);
      } else {
        setSuggestions(prev => [...prev, ...result.suggestions]);
      }

      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadStatusCounts = async () => {
    try {
      const counts = await suggestionService.getStatusCounts();
      setStatusCounts(counts);
    } catch (error) {
      console.error('Failed to load status counts:', error);
    }
  };

  const handleUpvote = async (suggestionId: string) => {
    if (!user) return;

    try {
      const result = await suggestionService.toggleUpvote(suggestionId, user.email!);
      
      // Update local state
      setSuggestions(prev => 
        prev.map(suggestion => 
          suggestion.id === suggestionId 
            ? {
                ...suggestion,
                upvoteCount: result.newCount,
                upvotes: result.isUpvoted 
                  ? [...(suggestion.upvotes || []), user.email!]
                  : (suggestion.upvotes || []).filter(email => email !== user.email!)
              }
            : suggestion
        )
      );
    } catch (error) {
      console.error('Failed to toggle upvote:', error);
    }
  };

  const handleAddSuccess = () => {
    loadSuggestions(true);
    loadStatusCounts();
  };

  const handleAdminManage = (suggestion: Suggestion) => {
    setManagingSuggestion(suggestion);
    setShowAdminModal(true);
  };

  const handleAdminSuccess = () => {
    loadSuggestions(true);
    loadStatusCounts();
  };

  const handleDelete = async (suggestionId: string) => {
    if (!user) return;

    try {
      await suggestionService.deleteSuggestion(suggestionId, user.email!);
      loadSuggestions(true);
      loadStatusCounts();
    } catch (error) {
      console.error('Failed to delete suggestion:', error);
    }
  };

  const handleUserEdit = (suggestion: Suggestion) => {
    setEditingSuggestion(suggestion);
    setShowEditModal(true);
  };

  const handleUserDelete = async (suggestionId: string) => {
    if (!user) return;
    
    if (window.confirm('Are you sure you want to delete your suggestion? This action cannot be undone.')) {
      try {
        await suggestionService.deleteSuggestion(suggestionId, user.email!);
        loadSuggestions(true);
        loadStatusCounts();
      } catch (error) {
        console.error('Failed to delete suggestion:', error);
      }
    }
  };

  const handleEditSuccess = () => {
    loadSuggestions(true);
    loadStatusCounts();
  };

  const totalSuggestions = statusCounts.open + statusCounts.underReview + statusCounts.implemented + statusCounts.declined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading suggestions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <MessageCircle size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-foreground">Suggestion Box</h2>
            <p className="text-muted-foreground">
              {totalSuggestions} suggestions • {statusCounts.open} open • {statusCounts.implemented} implemented
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
        >
          <Plus size={20} />
          Add Suggestion
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('latest')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'latest'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock size={16} />
            Latest
          </button>
          <button
            onClick={() => setActiveTab('popular')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'popular'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <TrendingUp size={16} />
            Popular
          </button>
        </nav>
      </div>

      {/* Empty State */}
      {suggestions.length === 0 && !loading && (
        <div className="text-center py-12">
          <MessageCircle size={64} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No suggestions yet</h3>
          <p className="text-muted-foreground mb-4">
            Be the first to share an idea to improve our workflow!
          </p>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Add First Suggestion
          </button>
        </div>
      )}

      {/* Suggestions List */}
      <div className="space-y-6">
        {suggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            onUpvote={handleUpvote}
            onAdminManage={handleAdminManage}
            onUserEdit={handleUserEdit}
            onUserDelete={handleUserDelete}
            currentUser={user?.email || ''}
            canManage={permissions.canManageContent}
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && suggestions.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={() => loadSuggestions(false)}
            disabled={loadingMore}
            className="bg-secondary text-secondary-foreground px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-secondary/80 disabled:opacity-50 border"
          >
            {loadingMore ? (
              <>
                <Loader className="animate-spin" size={16} />
                Loading more...
              </>
            ) : (
              'Load More Suggestions'
            )}
          </button>
        </div>
      )}

      {/* Add Suggestion Modal */}
      <AddSuggestionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Edit Suggestion Modal */}
      <EditSuggestionModal
        isOpen={showEditModal}
        suggestion={editingSuggestion}
        onClose={() => {
          setShowEditModal(false);
          setEditingSuggestion(null);
        }}
        onSuccess={handleEditSuccess}
      />

      {/* Admin Management Modal */}
      {permissions.canManageContent && (
        <AdminManageModal
          isOpen={showAdminModal}
          suggestion={managingSuggestion}
          onClose={() => {
            setShowAdminModal(false);
            setManagingSuggestion(null);
          }}
          onSuccess={handleAdminSuccess}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};